import { useCallback, useState } from 'react';

export interface RegistryColumnDef<T extends string = string> {
    id: T;
    defaultVisible?: boolean;
    /** Старий прапорець '1'/'0'. Читається лише коли нового JSON ще немає. */
    legacyKey?: string;
}

const readLegacyFlag = (key: string): boolean | null => {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null) return null;
        if (raw === '1' || raw === 'true') return true;
        if (raw === '0' || raw === 'false') return false;
        return null;
    } catch {
        return null;
    }
};

const readStored = (storageKey: string): Record<string, boolean> | null => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (raw === null) return null;
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
        const result: Record<string, boolean> = {};
        Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
            if (typeof value === 'boolean') result[key] = value;
        });
        return result;
    } catch {
        return null;
    }
};

const writeStored = (storageKey: string, patch: Record<string, boolean>) => {
    try {
        const prev = readStored(storageKey) ?? {};
        localStorage.setItem(storageKey, JSON.stringify({ ...prev, ...patch }));
    } catch {
        /* ignore */
    }
};

const loadColumns = <T extends string>(
    storageKey: string,
    defs: readonly RegistryColumnDef<T>[],
): Record<T, boolean> => {
    const stored = readStored(storageKey);
    const visible = {} as Record<T, boolean>;

    defs.forEach((def) => {
        if (stored && typeof stored[def.id] === 'boolean') {
            visible[def.id] = stored[def.id];
            return;
        }
        if (stored === null && def.legacyKey) {
            const legacy = readLegacyFlag(def.legacyKey);
            if (legacy !== null) {
                visible[def.id] = legacy;
                return;
            }
        }
        visible[def.id] = Boolean(def.defaultVisible);
    });

    if (stored === null) writeStored(storageKey, visible);
    return visible;
};

export const useRegistryColumns = <T extends string>(
    storageKey: string,
    defs: readonly RegistryColumnDef<T>[],
) => {
    const [visible, setVisible] = useState<Record<T, boolean>>(() => loadColumns(storageKey, defs));

    const toggle = useCallback((id: T) => {
        setVisible((prev) => {
            const next = { ...prev, [id]: !prev[id] };
            writeStored(storageKey, next);
            return next;
        });
    }, [storageKey]);

    return { visible, toggle };
};
